import { Spin } from "antd";
import i18n from "i18next";
import { type FC, useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router";

import type { CardProfileType } from "src/app/features/ApplicationProfile/subFeatures/CardProfile/types/CardProfileType";
import {
    usePromptDataLoadQuery,
    usePromptDataUpdateMutation,
} from "src/app/features/ApplicationProfile/subFeatures/PromptData/services/server/PromptDataApi";
import type { PromptDataQueryParams } from "src/app/features/ApplicationProfile/subFeatures/PromptData/services/server/PromptDataQueryTypes";
import { FormManager } from "../../../../../../../components/Form/FormManager";
import { useFormSubmitCompletion } from "../../../../../../../components/Form/hooks/useFormSubmitCompletion";
import { useLoadFormDefinitionQuery } from "../../../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../../../components/Form/types";
import type { FormProps } from "../../../../../../../components/Form/types/FormProps";
import { ResourceName } from "../../../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../../../core/router/types/routerTypes";
import type { PromptDataEntity } from "../../types/PromptDataEntity";

export const PromptDataEditForm: FC<FormProps> = ({ runSubmit, formAction }) => {
    const { appProfileId, cardProfId, id } = useParams();
    const [searchParams] = useSearchParams();
    const type = searchParams.get("type") as CardProfileType;

    const [queryConfig, setQueryConfig] = useState<PromptDataQueryParams>({} as PromptDataQueryParams);

    useEffect(() => {
        if (appProfileId && cardProfId && id) {
            setQueryConfig({ appProfileId, cardProfId, id });
        }
    }, [appProfileId, cardProfId, id]);

    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("prompt-data");

    const [formData, setFormData] = useState<PromptDataEntity>({} as PromptDataEntity);
    const { data: loadFormData, isFetching: loadFormIsInProgress } = usePromptDataLoadQuery({
        appProfileId,
        cardProfId,
        id,
    });

    useEffect(() => {
        if (loadFormData) {
            setFormData(loadFormData);
        }
    }, [loadFormData, setFormData]);

    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const backUrl = `${BASE_PATH}/${ResourceName.APPLICATION_PROFILE_LISTS}/${appProfileId}/${ResourceName.APPLICATION_PROFILE_CARD_DEFINITION_EFT4F}/${FORM_ACTION.EDIT}/${cardProfId}?type=${type}`;
    const completeSubmit = useFormSubmitCompletion(formAction, backUrl);
    const [saveFormData, { isLoading: saveFormIsInProgress }] = usePromptDataUpdateMutation();
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData || !queryConfig) {
                return;
            }

            const result = await saveFormData({
                promptDataFormData: formData,
                promptDataQuery: queryConfig,
            });

            const hasError = "error" in result;
            if (!hasError) {
                completeSubmit(
                    i18n.t("common.formUpdateSuccess", {
                        id: result.data.id,
                        resource: "PromptData",
                    }),
                );
            }
        },
        [formData, queryConfig, saveFormData, completeSubmit],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching || loadFormIsInProgress}>
            {formDefinition?.formConfiguration && (
                <FormManager<PromptDataEntity>
                    name="PromptDataEditForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
